import { AddOnServicesStatus, fetchThunk as fetchAddOnServicesThunk } from '@spike/addon-services-action';
import { fetchPetServicesThunk, NewBookingsStatus } from '@spike/new-bookings-action';
import { showErrorThunk, showWarningThunk } from '@spike/notifications-action';
import { startWaitlistConversion } from 'actions/waitlists/WaitlistsActions';
import { getWaitlistTimeSlots } from 'components/Waitlist/WaitlistForm/waitlistFormUtils';
import { useApiClientWrapper, useAuth, useTimeZone, useWaitForReduxChange } from 'hooks';
import { AppointmentMultiple, DataVaraible, Discount, ItemData } from 'model/NewMultipleBooking';
import { Waitlist } from 'model/Waitlist';
import moment, { Moment } from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from 'store';

const FLOWS: Record<string, ItemData['flow']> = {
    grooming: 'grooming',
    dental: 'dental',
    daycare: 'daycare',
    boarding: 'boarding',
    transportation: 'transportation',
    walking: 'walking',
    training: 'training'
};

const NO_DISCOUNT: Discount = { value: 0, type: 'amount' as Discount['type'] };

// Boarding keeps the nights: the check-out is the day after the last night, at the check-in time.
const getCheckOut = (nights: Array<string>, checkIn: Moment): Moment => {
    const lastNight = nights.length > 0 ? moment.tz(nights[nights.length - 1], 'YYYY-MM-DD', checkIn.tz()!) : checkIn;

    return checkIn
        .clone()
        .set({ year: lastNight.year(), month: lastNight.month(), date: lastNight.date() })
        .add(1, 'day');
};

/**
 * Convert to Booking: builds the Add Appointment draft from the waitlist and opens it.
 * Waitlist values are preferences: dates, staff and composition stay editable, and prices
 * come from the current pricing (the waitlist price is never used).
 */
export const useWaitlistConversion = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const auth = useAuth();
    const timeZone = useTimeZone();
    const { waitForStateChange } = useWaitForReduxChange();

    const schedule = useSelector((state: RootState) => state.marketplace.marketplace.schedule);

    const [converting, setConverting] = useState(false);

    // Starting the conversion closes the panel (and unmounts the detail that owns this hook).
    const mounted = useRef(true);

    useEffect(
        () => () => {
            mounted.current = false;
        },
        []
    );

    // A requested date at the Preferred Time, or the closest sensible start when they are ASAP / Anytime.
    const getStartDate = (waitlist: Waitlist, requestedDate?: string): Moment => {
        const now = moment().tz(timeZone);
        const date = requestedDate ? moment.tz(requestedDate, 'YYYY-MM-DD', timeZone) : now.clone();

        if (!requestedDate && waitlist.preferredTime) {
            const start = moment.tz(
                `${date.format('YYYY-MM-DD')} ${waitlist.preferredTime}`,
                'YYYY-MM-DD HH:mm',
                timeZone
            );
            // ASAP with a time already gone today: tomorrow at that time.
            return start.isBefore(now) ? start.add(1, 'day') : start;
        }

        const time =
            waitlist.preferredTime ??
            (date.isSame(now, 'day')
                ? now
                      .clone()
                      .add(15 - (now.minute() % 15), 'minutes')
                      .format('HH:mm')
                : getWaitlistTimeSlots(schedule, date)[0] ?? '09:00');

        return moment.tz(`${date.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm', timeZone);
    };

    const convert = async (waitlist: Waitlist) => {
        setConverting(true);

        try {
            const petIds = Array.from(new Set(waitlist.services.map(service => service.petId)));
            const appointments: Array<AppointmentMultiple> = [];
            const unavailable: Array<string> = [];

            // Bookings doesn't load the add-on catalog on its own.
            if (waitlist.services.some(service => service.addons.length > 0)) {
                dispatch(fetchAddOnServicesThunk(apiClientWrapper));
                await waitForStateChange(state => state.addOnServices.status, AddOnServicesStatus.FetchSuccess);
            }

            const addOnServices = store.getState().addOnServices.services;

            for (const petId of petIds) {
                dispatch(fetchPetServicesThunk(apiClientWrapper, petId));
                await waitForStateChange(state => state.newBookings.status, NewBookingsStatus.FetchPetServicesSuccess);

                const petServices = store.getState().newBookings.petServices;

                waitlist.services
                    .filter(service => service.petId === petId)
                    .forEach(service => {
                        const petService = petServices.find(current => current.service.id === service.serviceId);

                        if (!petService) {
                            unavailable.push(service.serviceName);
                            return;
                        }

                        // One day already gone voids all the service's dates: it keeps New Booking's default (now).
                        const today = moment().tz(timeZone);
                        const hasPastDates = service.dates.some(date =>
                            moment.tz(date, 'YYYY-MM-DD', timeZone).isBefore(today, 'day')
                        );
                        const dates = hasPastDates ? [] : [...service.dates].sort();
                        // Add Appointment books one daycare day per item: each requested day is its own item.
                        const starts = hasPastDates
                            ? [today]
                            : service.businessArea === 'daycare' && dates.length > 0
                            ? dates.map(date => getStartDate(waitlist, date))
                            : [getStartDate(waitlist, dates[0])];

                        // Preselect the service's preferred staff only where they can do it.
                        const staffId =
                            service.preferredStaffEligible && service.preferredStaffId !== null
                                ? service.preferredStaffId
                                : undefined;

                        const addOns = service.addons.flatMap(addon => {
                            const addOnService = addOnServices.find(current => current.id === addon.addonServiceId);

                            if (!addOnService) {
                                unavailable.push(addon.addonServiceName);
                            }

                            return addOnService ? [addOnService] : [];
                        });

                        const getAddons = (date: Moment): Array<DataVaraible> =>
                            addOns.map(addOn => {
                                // The catalog price comes as a string ("15.0"), as in the Add-on selector.
                                const price = Number(addOn.price) || 0;

                                return {
                                    addOn,
                                    staffId,
                                    date,
                                    itemData: { discount: NO_DISCOUNT, quantity: 1, price, total: price }
                                };
                            });

                        starts.forEach(date =>
                            appointments.push({
                                petId,
                                clientId: waitlist.customerId,
                                createdByStaffId: auth.user?.staffId ?? 0,
                                notes: waitlist.notes ?? undefined,
                                variableData: [
                                    {
                                        staffId,
                                        date,
                                        dateEnd:
                                            service.businessArea === 'boarding' ? getCheckOut(dates, date) : undefined,
                                        service: {
                                            service: petService.service,
                                            price: petService.price,
                                            durationInMinutes: petService.durationInMinutes,
                                            businessArea: petService.businessArea,
                                            color: petService.color
                                        },
                                        itemData: { flow: FLOWS[service.businessArea], discount: NO_DISCOUNT }
                                    },
                                    ...getAddons(date)
                                ]
                            })
                        );
                    });
            }

            if (appointments.length === 0) {
                dispatch(
                    showErrorThunk('None of the waitlist services are available anymore. Edit the waitlist first.')
                );
                return;
            }

            if (unavailable.length > 0) {
                dispatch(showWarningThunk(`Not available anymore and left out: ${unavailable.join(', ')}.`));
            }

            dispatch(startWaitlistConversion(waitlist, appointments));
        } catch {
            dispatch(showErrorThunk('The waitlist services could not be loaded. Please try again.'));
        } finally {
            mounted.current && setConverting(false);
        }
    };

    return { convert, converting };
};

export default useWaitlistConversion;
